/**
 * Module dependencies.
 */

import { Popconfirm } from 'antd';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { useCrudDelete } from 'app/hooks/app-requests/crud/use-crud-delete';
import { useCrudList } from 'app/hooks/app-requests/crud/use-crud-list';
import { QueryOrderItem } from 'app/components/organisms/table/row';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CrudTemplate } from './form-types';
import { castArray, merge } from 'lodash';
import styles from './crud.module.less';
import { Table } from 'app/components/organisms/table/table';
import { Button } from 'app/components/atoms/button/button';
import { CrudFilters } from './form-filters/crud-filters';
import { Wrapper } from 'app/components/atoms/wrapper/wrapper';
import { Loading } from 'app/components/atoms/loading/loading';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useDebounceLoading } from 'app/hooks/use-debounce';
import classnames from 'classnames';
import { useQueryParamsStorageProvider } from 'app/components/providers/query-params-storage/query-params-storage';
import { defaultResolveParams } from 'app/core/utils/params';
import { Select } from 'app/components/atoms/forms/fields/select/select';
import { Country } from 'app/components/atoms/country/country';
import { useGetLanguages } from 'app/hooks/app-requests/use-get-languages';
import { Language } from 'app/types/language';
import { cookieStorage } from 'app/core/utils/cookie-storage';

/**
 * `Props` type.
 */

type Props<T> = {
  template: CrudTemplate<T>;
  nestedParams?: any;
};

/**
 * `ExpandedRowProps` type.
 */

type ExpandedRowProps = {
  item: any;
  extras: any;
  expanded: boolean;
  currentLanguage: string;
};

/**
 * `ExpandedRow` component.
 */

function ExpandedRow({ extras, item, currentLanguage, expanded }: ExpandedRowProps) {
  const list = extras.template.list;
  const columns = list.expandedColumns;
  const rowKey = list.expandedRowKey ?? 'id';
  const dataKey = list.expandedDataKey;
  const data = useMemo(() => {
    if (typeof dataKey === 'function') {
      return dataKey(item);
    }

    return item[dataKey];
  }, [dataKey, item]);

  if (!expanded) {
    return null;
  }

  return (
    <Table
      className={styles.nestedTable}
      columns={columns}
      currentLanguage={currentLanguage}
      dataSource={data}
      rowKey={rowKey}
    />
  );
}

/**
 * `ExpandedRowTable` component.
 */

function ExpandedRowTable({ extras, item, expanded }: { item: any; extras: any; expanded: boolean }) {
  const template = extras.template.list.expandedTemplate;
  const localList = !template.list.endpoint;
  const nestedParams = useMemo(() => {
    return {
      ...(template.list?.nestedParams?.(item) ?? {}),
      ...(localList ? { item } : {})
    };
  }, [item, localList, template.list]);

  if (!expanded) {
    return null;
  }

  if (!localList && !nestedParams) {
    throw new Error('`nestedParams` is required for `expandedTemplate`');
  }

  return (
    <div className={styles.nestedTable}>
      <CrudList nestedParams={nestedParams} template={template} />
    </div>
  );
}

/**
 * Export `CrudList` component.
 */

export function CrudList<T>({ template, nestedParams }: Props<T>): JSX.Element {
  const [translate] = useTranslation();
  const routerParams = useParams();
  const { setPageParams } = useQueryParamsStorageProvider();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const strRouterParams = useMemo(() => JSON.stringify(routerParams), [routerParams]);
  const params = useMemo(
    () => ({
      uuid: new Date().getTime(),
      ...JSON.parse(strRouterParams),
      ...(nestedParams ?? {})
    }),
    [nestedParams, strRouterParams]
  );

  const [pageSize, changePageSize] = useState(template.list?.pagination?.pageSize ?? 10);
  const lastTotal = useRef<number | undefined>();
  const pagingTimestamp = useRef<string | null>(null);
  const lastPage = useRef<number>(1);
  const isInitialMount = useRef<boolean>(true);
  const [liveMode, setLiveMode] = useState(false);
  const [isUserRefetch, setIsUserRefetch] = useState(false);
  const { mutate: deleteAction, isLoading } = useCrudDelete(template, params);
  const [query, setQuery] = useState<any>();
  const sortKey = searchParams.get('sortKey');
  const sortDirection = (searchParams.get('sortDirection') ?? 'asc') as 'asc' | 'desc';
  const columnOrderRef = useRef<QueryOrderItem[]>([]);
  if (sortKey) {
    const col = (template.list?.columns ?? []).find(c => c.key === sortKey);
    const item: QueryOrderItem = { key: sortKey, direction: sortDirection };

    if (col?.caseInsensitive) {
      item.caseInsensitive = true;
    }

    columnOrderRef.current = [item];
  } else {
    columnOrderRef.current = [];
  }
  const { data, refetch, ...restQuery } = useCrudList<T>(template, params, query);
  const appendFilters = useCallback(
    (values: any) => {
      if (!values) {
        setQuery({});
        return;
      }

      const { liveMode: liveModeValue, filters, ...restValues } = values;
      const query = merge({}, template.list?.append ?? {}, restValues);

      // Keep filters nested under 'filters' key instead of flattening
      if (filters && Object.keys(filters).length > 0) {
        query.filters = filters;
      }

      // Column sort overrides filter-derived order
      if (columnOrderRef.current.length > 0) {
        query.order = columnOrderRef.current;
      }

      // Update live mode state
      if (liveModeValue !== undefined) {
        setLiveMode(liveModeValue);
      }

      if (template.list?.pagination) {
        const currentPage = parseInt(searchParams.get('page') || '1', 10);
        query.page = currentPage;
        query.pageSize = pageSize;

        // Time-based pagination: freeze dataset using timestamp from page 1
        if (template.list.pagination.pagingByTime) {
          // Check if timestamp exists in query string
          const existingTimestamp = searchParams.get('pagingTimestamp');

          if (currentPage === 1) {
            // Page 1: create new timestamp if:
            // 1. Coming from another page (lastPage.current !== 1)
            // 2. No existing timestamp
            // 3. Initial mount (page refresh) - always get fresh data
            if (lastPage.current !== 1 || !existingTimestamp || isInitialMount.current) {
              pagingTimestamp.current = new Date().toISOString();
              isInitialMount.current = false;
            } else {
              // Same page 1, reuse existing timestamp (e.g., filter change)
              pagingTimestamp.current = existingTimestamp;
            }
          } else {
            // Pages 2+: use timestamp from query string or ref
            if (existingTimestamp) {
              pagingTimestamp.current = existingTimestamp;
            }
            isInitialMount.current = false;
          }

          // Update last page tracker
          lastPage.current = currentPage;

          // Include timestamp in query for all pages
          if (pagingTimestamp.current) {
            query.pagingTimestamp = pagingTimestamp.current;
            query.pagingTimeField = template.list.pagination.timeField || 'createdAt';
          }
        }
      }

      setQuery((data: any) => {
        if (JSON.stringify(data) === JSON.stringify(query)) {
          return data;
        }

        if (template?.list?.endpoint) {
          setPageParams(template?.list?.endpoint, query);
        }

        // Store pagingTimestamp in URL for consistency (only if different)
        if (template.list?.pagination?.pagingByTime && query.pagingTimestamp) {
          const currentTimestampInUrl = searchParams.get('pagingTimestamp');
          if (currentTimestampInUrl !== query.pagingTimestamp) {
            setSearchParams(params => {
              params.set('pagingTimestamp', query.pagingTimestamp);
              return params;
            });
          }
        }

        return query;
      });
    },
    [
      pageSize,
      searchParams,
      setPageParams,
      setSearchParams,
      template.list?.append,
      template.list?.endpoint,
      template.list?.pagination
    ]
  );

  const handleSortChange = useCallback(
    (key: string) => {
      setSearchParams(params => {
        const currentKey = params.get('sortKey');

        if (currentKey === key) {
          params.set('sortDirection', params.get('sortDirection') === 'asc' ? 'desc' : 'asc');
        } else {
          const col = (template.list?.columns ?? []).find(c => c.key === key);

          params.set('sortKey', key);
          params.set('sortDirection', col?.defaultSortDirection ?? 'asc');
        }

        return params;
      });
    },
    [setSearchParams, template.list?.columns]
  );

  // Auto-init sort when template has exactly one sortable column
  useEffect(() => {
    if (searchParams.get('sortKey')) return;

    const sortable = (template.list?.columns ?? []).filter(col => col.sortable);

    if (sortable.length !== 1) return;

    setSearchParams(
      params => {
        params.set('sortKey', sortable[0].key);
        params.set('sortDirection', sortable[0].defaultSortDirection ?? 'asc');

        return params;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync sort URL params → query
  useEffect(() => {
    setQuery((q: any) => {
      if (!q) return q;

      const { order: _old, ...rest } = q;

      return columnOrderRef.current.length > 0 ? { ...rest, order: columnOrderRef.current } : rest;
    });
  }, [sortKey, sortDirection]);

  const page = searchParams.get('page') ?? 1;
  // Don't show loading spinner in live mode
  // Big indicator: initial load OR user-triggered refetch (filter/page changes)
  // Small spinner: automatic background refetch (window focus, timed)
  const isLoadingData = useDebounceLoading(
    liveMode ? false : restQuery.isLoading || (restQuery.isFetching && isUserRefetch),
    50,
    350
  );
  const isRefetchingData = useDebounceLoading(liveMode ? false : restQuery.isRefetching && !isUserRefetch, 50, 350);

  useEffect(() => {
    // If pagingByTime active, don't reset page when total changes
    if (template.list?.pagination?.pagingByTime) {
      return;
    }

    if (!(data as any)?.total) {
      return;
    }

    if (!lastTotal.current) {
      lastTotal.current = (data as any).total;

      return;
    }
    if (lastTotal.current !== (data as any)?.total) {
      lastTotal.current = (data as any).total;

      setSearchParams(params => {
        params.set('page', '1');

        return params;
      });
    }
  }, [data, setSearchParams, template.list?.pagination?.pagingByTime]);

  useEffect(() => {
    setQuery((query: any) => {
      // Don't update if query hasn't been initialized yet (missing pageSize means it hasn't been set by appendFilters)
      if (!query || (template.list?.pagination && !query.pageSize)) {
        return query;
      }

      // Only update if page actually changed
      if (query.page === page) {
        return query;
      }

      return {
        ...query,
        page
      };
    });
  }, [page, refetch, template.list?.pagination]);

  const lastQueryRef = useRef<string>('');

  useEffect(() => {
    if (!restQuery.isFetching) {
      setIsUserRefetch(false);
    }
  }, [restQuery.isFetching]);

  useEffect(() => {
    if (!query) {
      return;
    }

    const queryString = JSON.stringify(query);

    // Only refetch if query actually changed
    if (lastQueryRef.current === queryString) {
      return;
    }

    lastQueryRef.current = queryString;
    setIsUserRefetch(true);
    refetch();
  }, [query, refetch]);

  // Live mode: auto-refresh every second when on page 1
  useEffect(() => {
    if (!liveMode || !query) {
      return;
    }

    const currentPage = parseInt(searchParams.get('page') || '1', 10);

    if (currentPage !== 1) {
      return;
    }

    const interval = setInterval(() => {
      // Reset timestamp to get fresh data
      if (template.list?.pagination?.pagingByTime) {
        pagingTimestamp.current = new Date().toISOString();
        // Update query with new timestamp
        setQuery((currentQuery: any) => ({
          ...currentQuery,
          pagingTimestamp: pagingTimestamp.current
        }));
      } else {
        refetch();
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [liveMode, query, refetch, searchParams, template.list?.pagination?.pagingByTime]);

  const columns = useMemo(() => {
    const columns = [...(template.list?.columns ?? [])];
    const hasRenderActions = !!template.list?.renderActions;
    const canEdit = !!template?.list?.canEdit;
    const canRemove = !!template.list?.canRemove;

    if (!hasRenderActions && !canEdit && !canRemove) {
      return columns;
    }

    columns.push({
      title: translate('common.table.columns.actions'),
      key: 'actions',
      style: columns?.[0].style,
      render: (item: any) => {
        const resolveParams = template.list?.resolveParams ?? defaultResolveParams;
        const to = !template.edit ? null : resolveAppPath(template.edit?.route as any, resolveParams(params, item));
        const _canEdit =
          typeof template.list?.canEdit === 'function' ? template.list?.canEdit(item) : template.list?.canEdit;
        const _canRemove =
          typeof template.list?.canRemove === 'function' ? template.list?.canRemove(item) : template.list?.canRemove;

        return (
          <div className={styles.actions}>
            {_canEdit && template?.edit && to && (
              <Link
                onClick={event => {
                  event.stopPropagation();
                }}
                to={to}
              >
                <Button icon={<EditOutlined />} size={'small'} />
              </Link>
            )}

            {_canRemove && template.remove && (
              <Popconfirm
                okButtonProps={{ loading: isLoading }}
                {...{ onClick: (event: any) => event.stopPropagation() }}
                onConfirm={(event: any) => {
                  event.stopPropagation();
                  deleteAction(item);
                }}
                title={translate('removeAction')}
              >
                <Button danger icon={<DeleteOutlined />} size={'small'} />
              </Popconfirm>
            )}

            {template.list?.renderActions?.(item, template, refetch)}
          </div>
        );
      }
    });

    return columns;
  }, [deleteAction, isLoading, params, refetch, template, translate]);

  const WrapperComponent = template.list?.WrapperComponent ?? Wrapper;
  const expandedRowRender = useMemo(() => {
    if (template?.list?.expandedColumns) {
      return ExpandedRow;
    }

    if (template?.list?.expandedTemplate) {
      return ExpandedRowTable;
    }
  }, [template?.list?.expandedColumns, template?.list?.expandedTemplate]);

  const onClickLine = useMemo(() => {
    if (!template.list?.clickLine) {
      return undefined;
    }

    return (item: any) => {
      template?.list?.clickLine?.(item, { navigate, ...params });
    };
  }, [navigate, params, template.list]);

  const user = cookieStorage.get('user');
  const [currentLanguage, setCurrentLanguage] = useState(user.language);
  const { data: availableLanguages } = useGetLanguages();
  const languages = useMemo(() => {
    if (typeof template?.list?.languages === 'string' && template?.list?.languages === '*') {
      setCurrentLanguage(availableLanguages?.[0].code);
      return availableLanguages ?? [];
    }

    const langs = castArray(template?.list?.languages ?? []).map(item => item.toLowerCase());

    return (availableLanguages ?? []).filter(item => {
      return langs.includes(item.code.toLowerCase());
    });
  }, [availableLanguages, template?.list?.languages]);

  return (
    <WrapperComponent
      className={styles.wrapper}
      isLoading={restQuery.isLoading || restQuery.isFetching}
      refetch={refetch}
      template={template}
    >
      <CrudFilters
        appendFilters={appendFilters}
        hasNested={!!nestedParams}
        params={params}
        refresh={refetch}
        template={template}
      >
        {!template?.list?.languages ? null : (
          <>
            <Select
              name={'language'}
              onChange={setCurrentLanguage}
              options={languages?.map((item: Language) => ({
                value: item.code,
                searchValue: item.name,
                minimal: <Country item={item} />,
                label: <Country item={item} />
              }))}
              placeholder={translate('common.labels.language')}
              style={{ marginTop: 8, minWidth: 200, top: -9 }}
              value={currentLanguage}
            />
          </>
        )}
      </CrudFilters>

      <Loading isLoading={isLoading}>
        <Table
          changePageSize={changePageSize}
          className={styles.table}
          columns={columns}
          currentLanguage={currentLanguage}
          dataSource={(data as any)?.results ?? []}
          expandedExtras={{ template }}
          expandedRowRender={expandedRowRender}
          footer={template?.list?.renderLastLine?.({
            data: (data as any)?.results,
            total: (data as any)?.total,
            columns,
            template,
            refetch
          })}
          isLoading={isLoadingData}
          isRefetching={isRefetchingData}
          onClickLine={onClickLine}
          onSortChange={handleSortChange}
          order={columnOrderRef.current}
          pagination={
            template.list?.pagination && {
              total: (data as any)?.total,
              pageSize: pageSize,
              pageSizeOptions: template.list?.pagination?.pageSizeOptions
            }
          }
          rowKey={'id'}
          style={template.list?.style}
          wrapperClassName={classnames({
            [styles.nestedTable]: !!nestedParams
          })}
        />
      </Loading>
    </WrapperComponent>
  );
}
